/**
 * Hook do feed da Central no Portal do Parceiro (E21.1.3) + confirmação de
 * Leitura Obrigatória (E21.5.2).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { portalCentralApi } from "@/services/portalApi";

/** True se o erro é uma resposta HTTP com o status dado (shape do AxiosError). */
function isHttpStatus(error: unknown, code: number): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === code;
}

// M2 (revisão final do PR 4) — mesmo formato de UUID que o backend aceita
// como path param (`/portal/central/posts/{postId}`).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True se `id` tem forma de UUID. Um `?post=` que não é UUID nunca é um post
 * válido — melhor recusar no cliente do que deixar a string chegar ao
 * caminho da API: `?post=../documents/<uuid>/download` faz o browser
 * colapsar o `..` e mandar um GET autenticado pra outro endpoint (M2). */
export function isPostId(id: string): boolean {
  return UUID_RE.test(id);
}

class PostIdInvalidoError extends Error {
  constructor() {
    super("id do post não é um UUID válido");
    this.name = "PostIdInvalidoError";
  }
}

/** True se o post destacado deve virar "esta publicação não está mais
 * disponível" (M1+M2): 404 (post expirou/saiu da Audiência), 422 (o backend
 * recebeu um id que não parseia como UUID) ou o id nunca tendo sido um UUID
 * pra começo de conversa — os três têm o MESMO tratamento visual. Qualquer
 * OUTRO erro (rede, 5xx) é transiente: quem chama mostra "tentar de novo" e
 * NÃO tira o post da lista (M1). */
export function isPostUnavailableError(error: unknown): boolean {
  if (error instanceof PostIdInvalidoError) return true;
  return isHttpStatus(error, 404) || isHttpStatus(error, 422);
}

/**
 * PR 4 Task 5 — busca UM post da Central pelo id, tipicamente o `?post=<id>`
 * da URL (link de uma notificação do sininho). Sem `postId` fica desabilitada
 * — não dispara à toa. 404 é o caso normal (o post expirou ou saiu da
 * Audiência do parceiro entre o envio da notificação e o clique): não insiste
 * em retentativa, o chamador mostra o aviso de indisponível na hora.
 *
 * M2 — `postId` que não tem forma de UUID nunca dispara `getCentralPost`:
 * o `queryFn` rejeita ANTES de tocar a rede (nenhuma requisição HTTP sai).
 */
export function usePortalCentralPost(postId: string | null) {
  return useQuery({
    queryKey: ["portal", "central", "post", postId],
    queryFn: () => {
      if (!postId || !isPostId(postId)) {
        throw new PostIdInvalidoError();
      }
      return portalCentralApi.getCentralPost(postId);
    },
    enabled: !!postId,
    retry: (failureCount, error) => (isPostUnavailableError(error) ? false : failureCount < 1),
    staleTime: 30000,
  });
}

export function usePortalCentralFeed(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ["portal", "central", "feed", page, pageSize],
    queryFn: () => portalCentralApi.getFeed({ page, page_size: pageSize }),
    staleTime: 30000,
    placeholderData: keepPreviousData, // mantém a página anterior durante a troca (v5)
  });
}

/**
 * E21.5.2 — confirma a leitura de um post obrigatório e revalida o feed.
 *
 * Fix round 1 (PR 4 Task 5) — também invalida a query do POST DESTACADO
 * (`usePortalCentralPost`, `["portal","central","post", postId]`). Sem isso,
 * confirmar a leitura no card destacado (`?post=<id>`, vindo do link de uma
 * notificação) não atualizava esse card — ele tem sua PRÓPRIA query, fora da
 * paginação do feed, e com `refetchOnWindowFocus: false` (lib/queryClient.ts)
 * ficava congelado até um reload completo. Não há query de `pending-reads`
 * no frontend hoje (só o endpoint no backend) — nada pra invalidar aí ainda.
 */
export function useConfirmRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => portalCentralApi.confirmRead(postId),
    onSuccess: (_data, postId) => {
      qc.invalidateQueries({ queryKey: ["portal", "central", "feed"] });
      qc.invalidateQueries({ queryKey: ["portal", "central", "post", postId] });
    },
  });
}

/** E21.3.3 — Biblioteca de Documentos no portal (filtro opcional por categoria). */
export function usePortalDocuments(page = 1, pageSize = 20, category?: string) {
  return useQuery({
    queryKey: ["portal", "central", "documents", page, pageSize, category ?? ""],
    queryFn: () => portalCentralApi.getDocuments({ page, page_size: pageSize, category }),
    staleTime: 30000,
    placeholderData: keepPreviousData,
  });
}

/** E21.3.5 — confirma a leitura de um documento obrigatório e revalida a biblioteca. */
export function useConfirmReadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) => portalCentralApi.confirmReadDocument(docId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal", "central", "documents"] }),
  });
}

/**
 * E21.2.3 — Base de Conhecimento no portal: lista/busca de artigos publicados.
 * `q` = busca full-text PT-BR (ordena por relevância); `category` = filtro.
 */
export function usePortalArticles(page = 1, pageSize = 20, category?: string, q?: string) {
  return useQuery({
    queryKey: ["portal", "central", "articles", page, pageSize, category ?? "", q ?? ""],
    queryFn: () => portalCentralApi.getArticles({ page, page_size: pageSize, category, q }),
    staleTime: 30000,
    placeholderData: keepPreviousData,
  });
}

/** E21.2.3 — detalhe (com conteúdo) de um artigo; só busca quando há `id`. */
export function usePortalArticleDetail(id: string | null) {
  return useQuery({
    queryKey: ["portal", "central", "article", id],
    queryFn: () => portalCentralApi.getArticleDetail(id as string),
    enabled: !!id,
    staleTime: 30000,
  });
}

/** E21.2.4 — confirma a leitura de um artigo obrigatório e revalida lista + detalhe. */
export function useConfirmReadArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (articleId: string) => portalCentralApi.confirmReadArticle(articleId),
    onSuccess: (_d, articleId) => {
      qc.invalidateQueries({ queryKey: ["portal", "central", "articles"] });
      qc.invalidateQueries({ queryKey: ["portal", "central", "article", articleId] });
    },
  });
}
